import React from 'react'
import type { DateView, PickersLayoutProps } from '@mui/x-date-pickers'
import { DatePicker } from '@mui/x-date-pickers'
import { addYears, subYears } from 'date-fns'
import useFavurTranslation from 'hooks/useFavurTranslation'
import { getCurrentDate } from 'pages/AbsencePlanner/components/MonthSwitcherBar/MonthSelector/getCurrentDate'
import CalendarButton from 'redesign/components/atoms/Buttons/CalendarButton'
import { CalendarIcon } from 'redesign/components/atoms/Icon'
import ClickableInputField from 'redesign/components/atoms/clickable-input-field/clickable-input-field'
import CalendarTodayButton from 'redesign/components/molecules/calendar-today-button'
import type { MonthPickerProps } from 'redesign/components/molecules/month-picker/types'
import { formatDate } from 'redesign/shared/date'
import { DATE_FORMAT_VARIANTS } from 'redesign/shared/date/constants'
import { ONE, ZERO } from 'shared/constants'
import { newDateWithoutTime } from 'utils/date'
import MonthlyCalendarHeader from './month-calendar-header'
import { Container, CustomLayout, CustomPaper, DatePickerContainer } from './styles'
import { useDesktopMonthPicker } from './use-desktop-month-picker'

const DesktopMonthPicker: React.FC<MonthPickerProps> = ({
  label,
  defaultValue,
  onChange,
  handleToday,
  isButton,
  disabled,
  testId,
  error,
  fullWidth,
  baseDate,
  monthIndex,
}) => {
  const { t, locale } = useFavurTranslation()
  const { ref, open, placeholderDate, setOpen, setPlaceholderDate, Popper, validDate, setYearDate } =
    useDesktopMonthPicker({
      defaultValue,
    })
  const currentDate = baseDate ?? newDateWithoutTime()

  return (
    <Container ref={ref} fullWidth={fullWidth}>
      <DatePickerContainer>
        <DatePicker
          open={open}
          defaultValue={defaultValue}
          slots={{
            desktopPaper: CustomPaper,
            calendarHeader: () => (
              <MonthlyCalendarHeader
                addYear={() => setYearDate(addYears(validDate, 1))}
                subtractYear={() => setYearDate(subYears(validDate, 1))}
                year={validDate.getFullYear()}
              />
            ),
            field: () =>
              isButton ? (
                <CalendarButton handleCalendarAction={() => setOpen(true)} label={label} />
              ) : (
                <ClickableInputField
                  disabled={disabled}
                  error={error}
                  focused={open}
                  label={label}
                  onClick={() => setOpen(true)}
                  testId={testId}
                  value={
                    placeholderDate
                      ? formatDate({
                          date: placeholderDate as Date,
                          dateFormat: DATE_FORMAT_VARIANTS.shortMonth,
                          t,
                          locale,
                        })
                      : ''
                  }
                  fullWidth={fullWidth}
                  endIcon={CalendarIcon}
                />
              ),
            layout: CustomLayout as React.FC<PickersLayoutProps<Date | null, Date, DateView>>,
            popper: Popper,
          }}
          views={['month']}
          onClose={() => setOpen(false)}
          onMonthChange={(date) => {
            const currentYear = date.getFullYear()
            const updatedYear = validDate.getFullYear()
            const updatedDate = addYears(date, updatedYear - currentYear)

            onChange(updatedDate)
            setPlaceholderDate(updatedDate)
          }}
        />
      </DatePickerContainer>
      {handleToday && (
        <CalendarTodayButton
          handleToday={() => {
            handleToday()
            setPlaceholderDate(newDateWithoutTime())
          }}
          handlePreviousButton={() => {
            onChange(getCurrentDate(currentDate, (monthIndex ?? ZERO) - ONE))
          }}
          handleNextButton={() => {
            onChange(getCurrentDate(currentDate, (monthIndex ?? ZERO) + ONE))
          }}
        />
      )}
    </Container>
  )
}

export default DesktopMonthPicker
